import React, { useEffect, useMemo } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';

import {
	usePanel,
	usePanelWidth,
	useObjectSidebarActions,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';

import { useWidthResizeObserver } from './useWidthResizeObserver.tsx';

// the min-width should match the value of MIN_PANEL_WIDTH from '@confluence/object-sidebar-api/entry-points/useObjectSidebar'
const panelPushStyles = xcss({
	display: 'block',
	minWidth: '400px',
});

// Resizable mode reads its width from the `--object-sidebar-push-width`
// CSS custom property set via the `style` prop at render time, keeping all
// static styling at module scope per ADS conventions.
const resizablePanelPushStyles = xcss({
	display: 'block',
	minWidth: '400px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	width: 'var(--object-sidebar-push-width)',
});

export const ObjectSidebarPushPlaceholder = (): React.JSX.Element => {
	const { setPanelWidth } = useObjectSidebarActions();
	const [containerRef, width] = useWidthResizeObserver();
	const panel = usePanel();
	const storedPanelWidth = usePanelWidth();

	const isResizable = !!panel?.isResizable;

	useEffect(() => {
		// When the panel is user-resizable the store (driven by
		// ObjectSidebarResizeHandle) is the source of truth — writing the
		// observed width back would clobber the user's drag on layout reflow.
		if (isResizable) {
			return;
		}
		setPanelWidth(width);
	}, [width, setPanelWidth, isResizable]);

	// In resizable mode the placeholder mirrors the store-driven visible
	// width (= `panelWidth − CONTENT_RIGHT_PADDING`) via a CSS custom
	// property consumed by `resizablePanelPushStyles`.
	const dynamicPushStyle = useMemo(
		() =>
			isResizable
				? ({
						'--object-sidebar-push-width': `${Math.max(storedPanelWidth - 40, 400)}px`,
					} as React.CSSProperties)
				: undefined,
		[isResizable, storedPanelWidth],
	);

	return (
		<Box
			ref={containerRef}
			xcss={isResizable ? resizablePanelPushStyles : panelPushStyles}
			// Runtime-driven CSS custom property value; static styles live in xcss above.
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={dynamicPushStyle}
			testId="object-sidebar-push-placeholder"
		/>
	);
};
